import { Injectable } from "@angular/core";
import { HttpErrorResponse } from "@angular/common/http";
import { TranslateService } from "@ngx-translate/core";
import { AlertService } from "../alert/service/alert.service";
import { HttpClient } from "@angular/common/http";
import { firstValueFrom } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class PaymentErrorHandlerService {
  private static readonly FALLBACK_ERROR =
    "An unexpected payment error occurred. Please try again shortly.";

  constructor(
    private translate: TranslateService,
    private alert: AlertService,
    private http: HttpClient,
  ) {}

  handleError(error: HttpErrorResponse): void {
    if (error.error instanceof Blob) {
      this.handleBlobError(error.error);
      return;
    }

    const payload = error.error ?? error;
    const i18nCode = this.extractI18nCode(payload);
    const backendMessage = this.extractMessage(payload);
    if (i18nCode) {
      this.translateAndShow(i18nCode, backendMessage);
    } else {
      this.translateAndShow("PAYMENT_API.UNEXPECTED_ERROR", backendMessage);
    }
  }

  handleApiResponseError(body: any): void {
    const i18nCode = this.extractI18nCode(body);
    const backendMessage = this.extractMessage(body);
    if (i18nCode) {
      this.translateAndShow(i18nCode, backendMessage);
    } else {
      this.translateAndShow("PAYMENT_API.UNEXPECTED_ERROR", backendMessage);
    }
  }

  /**
   * Show error: 1) translated i18nErrorCode, 2) else backend message, 3) else generic.
   * Uses ngx-translate first, then direct i18n file lookup for robustness.
   */
  private async translateAndShow(
    key: string,
    backendMessage?: string | null,
  ): Promise<void> {
    const normalizedKey = key?.trim();
    const normalizedBackend = backendMessage?.trim() ?? "";
    const backendLooksLikeI18nKey =
      !!normalizedBackend && /^[A-Z0-9_]+\.[A-Z0-9_.]+$/.test(normalizedBackend);
    const fallback =
      normalizedBackend && !backendLooksLikeI18nKey
        ? normalizedBackend
        : PaymentErrorHandlerService.FALLBACK_ERROR;

    const sync = this.translate.instant(normalizedKey);
    if (sync && sync !== normalizedKey) {
      this.alert.showError(sync);
      return;
    }

    const lang = this.getActiveLang();
    const fromLang = await this.lookupFromLangFile(normalizedKey, lang);
    if (fromLang) {
      this.alert.showError(fromLang);
      return;
    }

    if (lang !== "en") {
      const fromDefault = await this.lookupFromLangFile(normalizedKey, "en");
      if (fromDefault) {
        this.alert.showError(fromDefault);
        return;
      }
    }

    this.alert.showError(fallback);
  }

  private extractMessage(payload: any): string | null {
    if (!payload || typeof payload !== "object") return null;
    if (typeof payload.message === "string" && payload.message.trim()) {
      return payload.message;
    }
    return this.extractMessage(payload.error) ?? this.extractMessage(payload.body);
  }

  private handleBlobError(blob: Blob): void {
    if (blob.type === "application/json") {
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const parsed = JSON.parse(reader.result as string);
          const i18nCode = this.extractI18nCode(parsed);
          const backendMessage = this.extractMessage(parsed);
          this.translateAndShow(
            i18nCode ?? "PAYMENT_API.UNEXPECTED_ERROR",
            backendMessage,
          );
        } catch {
          this.translateAndShow("PAYMENT_API.UNEXPECTED_ERROR", null);
        }
      };
      reader.onerror = () => {
        this.translateAndShow("PAYMENT_API.UNEXPECTED_ERROR", null);
      };
      reader.readAsText(blob);
    } else {
      this.translateAndShow("PAYMENT_API.UNEXPECTED_ERROR", null);
    }
  }

  private extractI18nCode(payload: any): string | null {
    if (!payload) return null;

    if (typeof payload === "string") {
      try {
        const parsed = JSON.parse(payload);
        return this.extractI18nCode(parsed);
      } catch {
        return null;
      }
    }

    if (typeof payload !== "object") return null;

    if (payload.i18nErrorCode && typeof payload.i18nErrorCode === "string") {
      return payload.i18nErrorCode.trim();
    }

    const nestedError = this.extractI18nCode(payload.error);
    if (nestedError) {
      return nestedError;
    }

    const nestedBody = this.extractI18nCode(payload.body);
    if (nestedBody) {
      return nestedBody;
    }

    return null;
  }

  private getActiveLang(): string {
    return (
      sessionStorage.getItem("lang") ||
      this.translate.currentLang ||
      this.translate.defaultLang ||
      "en"
    );
  }

  private async lookupFromLangFile(
    key: string,
    lang: string,
  ): Promise<string | null> {
    try {
      const dict = await firstValueFrom(
        this.http.get<Record<string, any>>(`/assets/i18n/${lang}.json`),
      );
      const value = this.resolveNestedKey(dict, key);
      if (typeof value === "string" && value.trim()) {
        return value;
      }
      return null;
    } catch {
      return null;
    }
  }

  private resolveNestedKey(dict: Record<string, any>, key: string): any {
    const parts = key.split(".");
    let cursor: any = dict;
    for (const part of parts) {
      if (!cursor || typeof cursor !== "object" || !(part in cursor)) {
        return null;
      }
      cursor = cursor[part];
    }
    return cursor;
  }
}
